import { useUser } from '@clerk/nextjs';
import Link from 'next/link';
import { Button } from '@/code/frontend/components/ui/button';
import { LinkRouter } from '@/code/lib/routes';
import { trpc } from '@/code/lib/utils/trpc';

/**
 * Resolves Post Manager access from its two feature flags.
 *
 * POST_FEATURE_NEEDS_PERMISSION is an upsell gate that OVERRIDES the regular
 * CAN_VIEW_POSTS flag: when it's on, the nav entry is shown (so the user can
 * click through) but the page renders {@link PostFeatureUpsell} instead of the
 * feature. When it's off, access follows CAN_VIEW_POSTS exactly as before.
 *
 * Shared by both nav bars and both Post Manager pages so the override logic
 * lives in one place.
 */
export function usePostManagerAccess(): {
  /** Both flag queries have resolved — gate nav rendering on this to avoid flicker. */
  isFetched: boolean;
  /** Show the Post Manager nav entry / allow the page to render. */
  showPostManager: boolean;
  /** Render the upsell notice instead of the feature (flag overrides CAN_VIEW_POSTS). */
  needsPlanUpgrade: boolean;
} {
  const canViewPosts =
    trpc.featureFlags.getIsFlagEnabledForUser.useQuery('CAN_VIEW_POSTS');
  const needsPermission = trpc.featureFlags.getIsFlagEnabledForUser.useQuery(
    'POST_FEATURE_NEEDS_PERMISSION',
  );

  const needsPlanUpgrade = needsPermission.data === true;
  return {
    isFetched: canViewPosts.isFetched && needsPermission.isFetched,
    // Visible when the upsell gate is on (so users can reach the upsell) OR the
    // regular flag grants real access.
    showPostManager: needsPlanUpgrade || canViewPosts.data === true,
    needsPlanUpgrade,
  };
}

/** Plan-gated notice shown on the Post Manager pages when the org lacks the feature. */
export function PostFeatureUpsell() {
  const { user } = useUser();
  const orgSlug = user?.organizationMemberships?.[0]?.organization.slug;
  return (
    // min-h-[70vh] centers the content in the viewport (dynamic with page size)
    // — a plain h-full doesn't resolve a height inside the layout, so the text
    // sat at the top.
    <div className="flex min-h-[70vh] w-full flex-col items-center justify-center gap-6 bg-white p-8">
      <div className="max-w-md space-y-1 text-center text-gray-600">
        <p>
          This feature is not enabled on your Organization&apos;s Aurium plan.
        </p>
        <p>
          Please contact your Organization Admin or{' '}
          <a
            href="mailto:support@aurium.ai"
            className="font-medium text-primary hover:underline"
          >
            support@aurium.ai
          </a>{' '}
          for more details.
        </p>
      </div>
      {orgSlug && (
        <Button asChild>
          <Link href={LinkRouter.routeForOrganizationHome(orgSlug)}>
            Return to Dashboard
          </Link>
        </Button>
      )}
    </div>
  );
}
